import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { LoaderSymbol } from '../../components/loading';
import { DEFAULT_DATE_RANGE } from '../../components/listDefaults';
import { DateRangeSelect, TRACKING_RANGES } from '../modules/DateRangeSelect';
import { useNotify } from '../../components/notifications';
import { useSaveFlow } from '../../components/confirm';
import { useNavActions } from '../../hooks/useNavActions';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface PhCheckRow {
  oid:         string;
  jobId:       string;
  sampleId:    string;
  division:    string;
  containerId: string;
  phCheck:     string | null;
  statusName:  string;
  statusId:    string;
}

// 'All' or an exact lab Status value (e.g. "Pending Sampling", "Submitted",
// "Submitted, Subcontracted") — mirrors legacy's per-status column filter
// (Samplecheckin.Index.Status), NOT pH-recorded state.
type StatusFilter = string;
type DateRangeKey = '1M' | '3M' | '6M' | '1Y' | '2Y' | '5Y' | 'All';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
// The date-range window as a MONTHS count. Sent to the API so the SP computes the
// cutoff from the DB SERVER date (GoLive parity: DateTime.Today.AddMonths(-N)),
// NOT the browser's date which drifts a calendar-day across timezones (that day
// drift dropped ~50 rows vs GoLive, 2026-08-12). 'All' → no filter.
const RANGE_MONTHS: Record<DateRangeKey, number | null> = {
  '1M': 1, '3M': 3, '6M': 6, '1Y': 12, '2Y': 24, '5Y': 60, 'All': null,
};

function hasPh(v: string | null): boolean {
  return !!v && v.trim().length > 0;
}

// Status → shared pill variant (backlog.css .bl-pill ok/warn/alert/info) — the
// ONLY coloured cell (BO UI Standard). Matches legacy's status families:
// Pending* = amber, plain Submitted = green, any compound "Submitted, X" (e.g.
// Subcontracted / Partially Subcontracted) = teal/info so it reads as a
// variant of Submitted rather than a wholly different state.
function statusPillVariant(statusName: string): string {
  const s = statusName.trim().toLowerCase();
  if (s.startsWith('pending'))          return 'warn';
  if (s === 'submitted')                return 'ok';
  if (s.startsWith('submitted,'))       return 'info';
  if (s.startsWith('cancel'))           return 'alert';
  return 'info';
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function SampleContainerPhCheckPage() {
  const [rows,      setRows]      = useState<PhCheckRow[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [loadMs,    setLoadMs]    = useState<number | null>(null);
  const [error,     setError]     = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRangeKey>(DEFAULT_DATE_RANGE.SampleTracking as DateRangeKey);
  const [statusFilt, setStatusFilt] = useState<StatusFilter>('All');   // lab Status (Pending Sampling / Submitted / …)
  const [search,    setSearch]    = useState('');
  const [selected,  setSelected]  = useState<Set<string | number>>(new Set());
  // Batch-edit buffer (legacy InlineEditMode="Batch"): pH edits accumulate here,
  // keyed by row Oid, and are NOT sent to the server until "Save" is clicked —
  // "Cancel" discards them. Matches the Navigation Manager pattern.
  const [phEdits,   setPhEdits]   = useState<Record<string, string>>({});
  const [saving,    setSaving]    = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const notify    = useNotify();
  const runSave   = useSaveFlow();
  // Module Access Control (Role-Based MAC) — Write gates recording a pH value.
  // Falls open (true) for admins / no MAC data, same as every other MAC-gated
  // form in this app; only tightens where a lab has configured a MAC level.
  const canWrite  = useNavActions('sampphcheck').write;

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      const params: Record<string, string> = {};
      const months = RANGE_MONTHS[dateRange];
      if (months != null) params.months = String(months);
      const res = await api.get('/api/v1/sample-container-ph-check', { params });
      setRows((res.data as { data: PhCheckRow[] }).data ?? []);
      setPhEdits({});
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { void load(); }, [load]);

  // ── Inline pH edit — click-to-edit stages the value in the local batch
  // buffer (legacy InlineEditMode="Batch"); nothing hits the server until Save. ──
  const stagePhEdit = useCallback((row: PhCheckRow, newValue: string) => {
    const trimmed = newValue.trim();
    setPhEdits(prev => {
      const next = { ...prev };
      // No change from the row's current (already-saved) value → not dirty.
      if (trimmed === (row.phCheck ?? '')) delete next[row.oid];
      else next[row.oid] = trimmed;
      return next;
    });
  }, []);

  const dirtyCount = Object.keys(phEdits).length;

  // "Cancel changes" — discard every staged edit, back to last-loaded values,
  // and clear row selection too (checking a row while editing shouldn't leave
  // it looking "armed" once the edit itself has been discarded).
  const cancelEdits = useCallback(() => {
    setPhEdits({});
    setSelected(new Set());
  }, []);

  // "Save changes" — the shared save flow (confirm → audited PUT per row →
  // "Saved successfully.") — same pattern as Navigation Manager's batch save.
  const save = useCallback(() => {
    if (dirtyCount === 0) { notify.info('No changes to save.'); return; }
    setSaving(true);
    runSave({
      formName: 'Sample Container pH Check',
      auditAction: 'edit',
      save: async () => {
        for (const [oid, value] of Object.entries(phEdits)) {
          await api.put(`/api/v1/sample-container-ph-check/${oid}`, { phCheck: value || null });
        }
        await load();
        setSelected(new Set());
      },
      successMessage: `Updated ${dirtyCount} container(s).`,
    }).finally(() => setSaving(false));
  }, [dirtyCount, phEdits, notify, runSave, load]);

  // ── Derived data ───────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let r = rows;
    if (statusFilt !== 'All') r = r.filter(x => x.statusName === statusFilt);
    if (search.trim()) {
      const t = search.toLowerCase();
      r = r.filter(x =>
        x.jobId.toLowerCase().includes(t) ||
        x.sampleId.toLowerCase().includes(t) ||
        x.containerId.toLowerCase().includes(t) ||
        x.statusName.toLowerCase().includes(t)
      );
    }
    return r;
  }, [rows, statusFilt, search]);

  // The grid's OWN column-filtered + sorted set (pre-pagination) — kept in sync
  // via onVisibleRowsChange so "Export to" exports exactly what's on screen,
  // including any active column filter (e.g. Sample ID), not just the
  // page-level search/status-filtered rows.
  const [visibleRows, setVisibleRows] = useState<PhCheckRow[]>(filtered);

  // Distinct lab Status values present in the data, with counts — mirrors
  // legacy's per-status filter breakdown (e.g. Pending Sampling / Submitted /
  // Submitted, Subcontracted) instead of a fixed, hardcoded set, so any
  // status family that appears just shows up as its own chip.
  const statusBreakdown = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) counts.set(r.statusName, (counts.get(r.statusName) ?? 0) + 1);
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  const checkedCount = useMemo(() => rows.filter(x => hasPh(x.phCheck)).length, [rows]);
  const pendingCount = useMemo(() => rows.length - checkedCount, [rows, checkedCount]);
  const checkedPct   = rows.length ? Math.round(checkedCount / rows.length * 100) : 0;

  // Shared-grid columns — pH is the ONE inline-editable cell (click-to-edit,
  // shared <InlineCellEditor>); everything else is read-only display. The pH
  // `value` accessor reflects any STAGED (unsaved) edit so the cell shows what
  // you typed even before Save is clicked.
  const cols: AppColumn<PhCheckRow>[] = [
    { field: 'jobId',       header: 'Job ID',       width: 100 },
    { field: 'sampleId',    header: 'Sample ID',    width: 130 },
    { field: 'containerId', header: 'Container ID', width: 110 },
    { field: 'phCheck',     header: 'pHCheck', width: 100,
      value: r => phEdits[r.oid] ?? (r.phCheck ?? ''),
      text: r => phEdits[r.oid] ?? (r.phCheck ?? ''),
      // Module Access Control gate: no Write → cell renders as plain text,
      // never opens the inline editor (server-side PUT is 403'd regardless).
      edit: { type: 'text', trigger: 'click', disabled: () => !canWrite } },
    { field: 'statusName',  header: 'Status', width: 200,
      text: r => r.statusName,
      render: r => <span className={`bl-pill ${statusPillVariant(r.statusName)}`}>{r.statusName}</span> },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="bl-page">

        {/* ── Fixed top: breadcrumb + title + KPIs ──────────────────────── */}
        <div className="bl-top">
          <div className="dp-page-hdr">
            <div className="dp-breadcrumb">
              <span>Home</span>
              <i className="ti ti-chevron-right" />
              <span>Sample Tracking</span>
              <i className="ti ti-chevron-right" />
              <span className="dp-bc-active">Sample Container pH Check</span>
            </div>
            {/* Shared FormTitle — the ONE page heading (§18.6): nav icon/colour
                via navMeta + the TOP load-time chip. */}
            <div className="dp-title-row">
              <FormTitle pageId="sampphcheck" auditFormName="Sample Container pH Check" loadMs={loadMs} loading={loading} />
              {/* Save / Cancel — batch-edit pattern (same as Navigation Manager):
                  disabled until a pH cell is edited. Sits top-right of the title
                  row, not the toolbar, so it stays visible while scrolling.
                  Hidden entirely (not just disabled) when Module Access Control
                  doesn't grant Write — same "hide, not disable" convention every
                  other MAC-gated button in this app follows. */}
              {canWrite && (
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button className="ca-btn ca-btn-solid" onClick={save} disabled={saving || dirtyCount === 0} title="Save changes">
                    {saving ? <><LoaderSymbol size={13} /> Saving…</> : <><span className="tb-i">💾</span> Save</>}
                  </button>
                  <button className="ca-btn" onClick={cancelEdits} disabled={saving || dirtyCount === 0} title="Cancel changes">
                    <span className="tb-i">✕</span> Cancel
                  </button>
                </div>
              )}
            </div>
            <div className="dp-subtitle">
              {canWrite
                ? <>Click a pH cell to record a reading · {loading ? '…' : rows.length} items · {dateRange}</>
                : <>Read-only — you don't have Write permission for Sample Container pH Check · {loading ? '…' : rows.length} items · {dateRange}</>}
            </div>
          </div>

          {/* KPI row */}
          <div className="kpi-row">
            <div className="kpi">
              <div className="kpi-lbl">Total ({dateRange})</div>
              <div className="kpi-val kpi-blue">{loading ? '…' : rows.length}</div>
              <div className="kpi-chg">Items in range</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">pH Recorded</div>
              <div className="kpi-val kpi-teal">{loading ? '…' : checkedCount}</div>
              <div className="kpi-chg" style={{ color: 'var(--ai-green)' }}>{checkedPct}%</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">pH Pending</div>
              <div className="kpi-val kpi-red">{loading ? '…' : pendingCount}</div>
              <div className="kpi-chg" style={{ color: 'var(--ai-amber)' }}>{100 - checkedPct}%</div>
            </div>
          </div>
        </div>

        {/* ── Grid area: toolbar + search sit ON the grey page (BO UI Standard
            v2); only the grid card below is white. ─────────────────────── */}
        <div className="bl-grid-area">
          {/* Toolbar — canonical order: [left] · Refresh · Export to. No
              New/Edit/Delete — the pH cell itself is the edit surface. */}
          <div style={{ margin: '0 0 8px' }}>
            <AppToolbar
              left={
                <DateRangeSelect variant="dropdown" label="Date Range" ranges={TRACKING_RANGES}
                  value={TRACKING_RANGES.find(r => r.label === dateRange)?.days ?? null}
                  onChange={(d) => setDateRange((TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All') as DateRangeKey)} />
              }
              onRefresh={load}
              refreshing={loading}
              exportData={{ filename: 'sample-container-ph-check', ...gridExportMatrix(visibleRows, cols) }}
            />
          </div>

          {/* Global search — shared .list-search-row on the grey page, never inside the card. */}
          <div className="list-search-row">
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input
                ref={searchRef}
                type="text"
                placeholder="Search job ID, sample ID, container, status…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <span className="list-search-clear" onClick={() => setSearch('')}>×</span>
              )}
            </div>
            {(filtered.length !== rows.length) && (
              <span className="search-match-count">
                {filtered.length} match{filtered.length !== 1 ? 'es' : ''}
              </span>
            )}
            {dirtyCount > 0 && (
              <span className="search-match-count"><b style={{ color: '#b5560c' }}>{dirtyCount} unsaved</b></span>
            )}
          </div>

          {/* ONE white grid card — filter-chip strip + grid + its single footer. */}
          <div className="bl-card bl-card-flex">

            {/* Status filter — shared .sa-filter-* chips (backlog.css). Chips are
                the DISTINCT lab Status values present (Pending Sampling /
                Submitted / Submitted, Subcontracted / …), matching legacy's
                own per-status breakdown — not pH-recorded state (see the pH
                Recorded / pH Pending KPI tiles above for that). */}
            <div className="sa-filter-row">
              <span className="sa-filter-lbl">Status:</span>
              <button
                className={`sa-filter-btn${statusFilt === 'All' ? ' active' : ''}`}
                onClick={() => setStatusFilt('All')}
              >
                All ({rows.length})
              </button>
              {statusBreakdown.map(([name, count]) => (
                <button
                  key={name}
                  className={`sa-filter-btn${statusFilt === name ? ' active' : ''}`}
                  onClick={() => setStatusFilt(name)}
                >
                  {name} ({count})
                </button>
              ))}
            </div>

            {/* Table — load failure stays inline; AppDataGrid handles the rest */}
            {error ? (
              <div className="bl-err" style={{ margin: 12 }}>
                <i className="ti ti-alert-circle" /> {error}
                <button onClick={load} style={{ marginLeft: 10, color: 'var(--brand-accent, #e07b39)', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Retry</button>
              </div>
            ) : (
              /* Shared data grid — styling, per-column filters, sort, footer /
                 pagination, load-time and inline editing are all standardised
                 in <AppDataGrid>. */
              <AppDataGrid<PhCheckRow>
                rows={filtered}
                columns={cols}
                getRowId={r => r.oid}
                loading={loading}
                loadMs={loadMs}
                selectable
                selectedIds={selected}
                onSelectionChange={setSelected}
                onVisibleRowsChange={setVisibleRows}
                onCellCommit={(row, column, newValue) => {
                  if (column.field === 'phCheck') stagePhEdit(row, newValue);
                }}
                // Stage on every keystroke too (not just blur/Enter) — Save
                // enables the moment you type, no need to click away first.
                onCellChange={(row, column, newValue) => {
                  if (column.field === 'phCheck') stagePhEdit(row, newValue);
                }}
                emptyText="No records found"
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
